import {TargetBlank} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import * as React from 'react';
import {useHistory} from 'react-router';

import emptyImg2x from '../assets/il-no-artifacts@2x.png';
import * as Generated from '../generated/graphql';
import {isVersionAlias} from '../util/artifacts';
import DOC_URLS from '../util/doc_urls';
import {propagateErrorsContext} from '../util/errors';
import {useResizer} from '../util/resize';
import * as Urls from '../util/urls';
import ArtifactSidebar, {
  artifactTypeNameSortFn,
} from './ArtifactSidebar/ArtifactSidebar';
import BlankPage from './BlankPage';
import {ArtifactPanelURLProvider} from './panels.domain/artifact/PanelArtifact/ArtifactPanelRoot';
import {InstrumentedCenteredWaveLoader as Loader} from './utility/InstrumentedLoader';

interface ProjectArtifactsProps {
  entityName: string;
  projectName: string;
  artifactTypeName?: string;
  artifactCollectionName?: string;
  artifactCollectionView?: string;
  membershipIdentifier?: string;
  artifactTab?: string;

  filePath?: string;
  compareArtifact?: {
    artifactCollectionName: string;
    membershipIdentifier: string;
  };
}

const ProjectArtifactTypes = (props: ProjectArtifactsProps) => {
  const artifactCollectionView =
    props.artifactCollectionView ??
    (props.membershipIdentifier ? 'membership' : 'versions');
  const doneRedirecting = useArtifactPathRedirectEffect({
    ...props,
    artifactCollectionView,
  });
  const {onMouseDown, cursor, dropSize} = useResizer('right', 270, {
    min: 250,
  });

  return (
    <div className="artifact-page">
      <div className="artifact-page-side-container">
        <div className="artifact-page-side" style={{width: dropSize}}>
          <ArtifactSidebar {...props} />
        </div>
        <div
          className="artifact-page-side-resizer"
          onMouseDown={onMouseDown}
          style={{cursor}}
        />
      </div>
      <div className="artifact-page-main">
        {!doneRedirecting ? (
          <Loader name="project-artifact-types-loader" size="huge" />
        ) : props.artifactCollectionName == null ||
          typeof props.artifactTypeName !== 'string' ? (
          <NoArtifactsMessage />
        ) : (
          <ArtifactPanelURLProvider
            projectName={props.projectName}
            entityName={props.entityName}
            artifactTypeName={props.artifactTypeName}
            artifactCollectionName={props.artifactCollectionName}
            selectedMembershipIdentifier={props.membershipIdentifier}
            selectedTab={props.artifactTab}
            selectedCollectionView={artifactCollectionView}
            filePath={props.filePath}
            compareArtifact={props.compareArtifact}
          />
        )}
      </div>
    </div>
  );
};

const NoArtifactsMessage = () => {
  React.useEffect(() => {
    window.analytics?.track('Empty State Viewed', {location: 'artifacts'});
  }, []);
  return (
    <BlankPage
      extraTopMargin
      header="Artifacts"
      subheader="Lightweight dataset and model versioning"
      info="Use Artifacts to track datasets and model versions. Artifacts can be saved in your own cloud storage or in the W&B backend."
      graphics={[
        {
          src: emptyImg2x,
          alt: 'no-artifacts',
        },
      ]}>
      <TargetBlank href="http://wandb.me/artifacts-quickstart">
        <Button
          variant="secondary"
          className="mx-5 p-8"
          onClick={() => {
            window.analytics?.track('See Example Clicked', {
              location: 'empty artifacts tab',
            });
          }}>
          Try Code
        </Button>
      </TargetBlank>
      <TargetBlank href={DOC_URLS.artifacts}>
        <Button
          variant="primary"
          className="mx-5 p-8"
          onClick={() => {
            window.analytics?.track('See Docs Clicked', {
              location: 'empty artifacts tab',
            });
            window.analytics?.track('See Guide Clicked', {
              location: 'artifacts empty tab',
            });
          }}>
          See Guide
        </Button>
      </TargetBlank>
    </BlankPage>
  );
};

const useArtifactPathRedirectEffect = (location: ProjectArtifactsProps) => {
  /**
   * This is how we handle redirection:
   * 1. If artifactTypeName is provided, verify that such type exists
   *    - If it does, proceed. Else, redirect to most recently created
   *      membership. If none exists, show empty
   * 2. If artifactCollectionName is provided, verify that such collection
   *    exists
   *    - If it does, proceed. Else, redirect to the most recent membership of
   *      this type. If none exists, the most recent membership over all. If
   *      none exists, show empty
   * 3. If membershipIdentifier is provided, verify that such membership exists.
   *   - If it does, proceed. Else, redirect to the most recent member in the
   *     provided collection, else empty membership.
   */
  const {
    entityName,
    projectName,
    artifactTypeName,
    artifactCollectionName,
    membershipIdentifier,
    artifactCollectionView,
  } = location;
  const history = useHistory();
  const artifactCollectionQuery = Generated.useArtifactRedirectQuery({
    variables: {
      entityName,
      projectName,
      artifactTypeName: artifactTypeName ?? '',
      artifactCollectionName: artifactCollectionName ?? '',
      membershipIdentifier: membershipIdentifier ?? '',
    },
    context: propagateErrorsContext(),
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const targetArtifactType =
    artifactCollectionQuery.data?.project?.artifactType ??
    artifactCollectionQuery.data?.project?.artifactTypes.edges.sort(
      (a: any, b: any) => {
        return artifactTypeNameSortFn(a.node.name, b.node.name);
      }
    )[0]?.node;
  // TODO: why is this any cast needed?
  const targetArtifactCollection =
    (targetArtifactType as any)?.artifactCollection ??
    targetArtifactType?.artifactCollections?.edges[0]?.node;
  const targetMembership =
    targetArtifactCollection?.artifactMembership ??
    targetArtifactCollection?.artifactMemberships?.edges[0]?.node;

  const targetArtifactTypeName: string | undefined = targetArtifactType?.name;
  const targetArtifactCollectionName: string | undefined =
    targetArtifactCollection?.name;
  const targetMembershipIdentifier: string | undefined =
    targetMembership != null ? `v${targetMembership?.versionIndex}` : undefined;

  const membershipNeedsRedirect =
    artifactCollectionView == null &&
    targetMembershipIdentifier !== membershipIdentifier &&
    (membershipIdentifier == null || isVersionAlias(membershipIdentifier));

  const needsRedirect =
    targetArtifactTypeName !== artifactTypeName ||
    targetArtifactCollectionName !== artifactCollectionName ||
    membershipNeedsRedirect;

  React.useEffect(() => {
    if (!artifactCollectionQuery.loading && needsRedirect) {
      if (
        location.artifactTab === 'files' &&
        targetArtifactTypeName != null &&
        targetArtifactCollectionName != null &&
        targetMembershipIdentifier != null
      ) {
        history.push(
          Urls.artifactFile({
            entityName,
            projectName,
            artifactTypeName: targetArtifactTypeName,
            artifactSequenceName: targetArtifactCollectionName,
            artifactCommitHash: targetMembershipIdentifier,
            path: location.filePath?.split('/') ?? [],
            compareArtifactSequence:
              location.compareArtifact?.artifactCollectionName ?? undefined,
            compareArtifactID:
              location.compareArtifact?.membershipIdentifier ?? undefined,
          })
        );
      } else if (
        location.artifactTab != null &&
        targetArtifactTypeName != null &&
        targetArtifactCollectionName != null &&
        targetMembershipIdentifier != null
      ) {
        history.replace(
          Urls.artifactTab({
            entityName,
            projectName,
            artifactTypeName: targetArtifactTypeName,
            artifactCollectionName: targetArtifactCollectionName,
            artifactCommitHash: targetMembershipIdentifier,
            tabName: location.artifactTab,
            compareArtifactSequence:
              location.compareArtifact?.artifactCollectionName ?? undefined,
            compareArtifactID:
              location.compareArtifact?.membershipIdentifier ?? undefined,
          })
        );
      } else {
        history.replace(
          Urls.artifactsPage({
            entityName,
            projectName,
            artifactTypeName: targetArtifactTypeName,
            artifactSequenceName: targetArtifactCollectionName,
            artifactCommitHash: targetMembershipIdentifier,
          })
        );
      }
    }
  }, [
    artifactCollectionName,
    artifactCollectionQuery.loading,
    artifactTypeName,
    entityName,
    history,
    location.artifactTab,
    location.compareArtifact,
    location.filePath,
    membershipIdentifier,
    needsRedirect,
    projectName,
    targetArtifactCollectionName,
    targetArtifactTypeName,
    targetMembershipIdentifier,
  ]);

  return !artifactCollectionQuery.loading && !needsRedirect;
};

export default ProjectArtifactTypes;
